import React, { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { BarChart3, Play, Square, CheckCircle, Users, Clock } from "lucide-react"
import { usePolls, usePollResults } from "@/hooks/usePolls"
import { useAuth } from "@/hooks/useAuth"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"

interface PollCardProps {
  poll: any
  canManage: boolean
  meetingStatus: string
}

export function PollCard({ poll, canManage, meetingStatus }: PollCardProps) {
  const { profile } = useAuth()
  const { updatePollStatus, vote, isVoting } = usePolls()
  const { data: pollResults } = usePollResults(poll.id)
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active": return "bg-green-500"
      case "draft": return "bg-gray-500"
      case "closed": return "bg-red-500"
      default: return "bg-gray-500"
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case "active": return "Ativa"
      case "draft": return "Rascunho"
      case "closed": return "Finalizada"
      default: return "Desconhecido"
    }
  }

  const hasUserVoted = poll.poll_votes?.some((vote: any) => vote.voter_id === profile?.id)

  const handleOptionSelect = (optionId: string) => {
    if (poll.poll_type === "single_choice" || poll.poll_type === "yes_no") {
      setSelectedOptions([optionId])
    } else {
      setSelectedOptions(prev => 
        prev.includes(optionId) 
          ? prev.filter(id => id !== optionId)
          : [...prev, optionId]
      )
    }
  }

  const handleVote = () => {
    if (selectedOptions.length > 0) {
      vote({ pollId: poll.id, optionIds: selectedOptions })
      setSelectedOptions([])
    }
  }

  const handleAbstention = () => {
    vote({ pollId: poll.id, voteType: "abstention" })
  }

  const canVote = poll.status === "active" && !hasUserVoted && meetingStatus === "live"
  const showResults = poll.status === "closed" || hasUserVoted || canManage

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <CardTitle className="text-xl">{poll.question}</CardTitle>
              <Badge className={`${getStatusColor(poll.status)} text-white`}>
                {getStatusText(poll.status)}
              </Badge>
            </div>
            {poll.description && (
              <p className="text-muted-foreground">{poll.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {pollResults?.results.total_votes || 0} votos
              </div>
              {poll.created_at && (
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {format(new Date(poll.created_at), "dd/MM 'às' HH:mm", { locale: ptBR })}
                </div>
              )}
              {poll.is_anonymous && (
                <Badge variant="outline">Anônima</Badge>
              )}
            </div>
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
              {poll.status === "draft" && (
                <Button 
                  size="sm"
                  onClick={() => updatePollStatus({ pollId: poll.id, status: "active" })}
                >
                  <Play className="w-4 h-4 mr-1" />
                  Iniciar
                </Button>
              )}
              {poll.status === "active" && (
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={() => updatePollStatus({ pollId: poll.id, status: "closed" })}
                >
                  <Square className="w-4 h-4 mr-1" />
                  Finalizar
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Voting Interface */}
        {canVote && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-900">Sua resposta:</h4>
            <div className="space-y-2">
              {poll.poll_options?.map((option: any) => (
                <label 
                  key={option.id}
                  className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <input
                    type={poll.poll_type === "single_choice" || poll.poll_type === "yes_no" ? "radio" : "checkbox"}
                    name={`poll-${poll.id}`}
                    value={option.id}
                    checked={selectedOptions.includes(option.id)}
                    onChange={() => handleOptionSelect(option.id)}
                    className="w-4 h-4"
                  />
                  <span className="text-blue-900">{option.option_text}</span>
                </label>
              ))}
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button 
                onClick={handleVote}
                disabled={selectedOptions.length === 0 || isVoting}
                size="sm"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                {isVoting ? "Votando..." : "Confirmar Voto"}
              </Button>
              {poll.allow_abstention && (
                <Button 
                  variant="outline"
                  onClick={handleAbstention}
                  disabled={isVoting}
                  size="sm"
                >
                  Abstenção
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Results */}
        {showResults && pollResults && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-5 h-5" />
              <h4 className="font-semibold">Resultados</h4>
            </div>
            
            {pollResults.results.option_votes.map((option: any) => (
              <div key={option.id} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{option.option_text}</span>
                  <div className="text-sm text-muted-foreground">
                    {option.vote_count} votos ({option.percentage.toFixed(1)}%)
                  </div>
                </div>
                <Progress 
                  value={option.percentage} 
                  className="h-2"
                />
              </div>
            ))}

            {poll.allow_abstention && pollResults.results.abstentions > 0 && (
              <div className="space-y-2 border-t pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Abstenções</span>
                  <div className="text-sm text-muted-foreground">
                    {pollResults.results.abstentions} votos ({pollResults.results.abstention_percentage.toFixed(1)}%)
                  </div>
                </div>
                <Progress 
                  value={pollResults.results.abstention_percentage} 
                  className="h-2 opacity-60"
                />
              </div>
            )}

            <div className="text-center text-sm text-muted-foreground pt-2 border-t">
              Total de participantes: {pollResults.results.total_votes}
            </div>
          </div>
        )}

        {/* Already Voted Message */}
        {hasUserVoted && poll.status === "active" && (
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Você já votou nesta enquete</span>
            </div>
            <p className="text-sm text-green-600 mt-1">
              Seus resultados serão exibidos quando a enquete for finalizada.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}